import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, DollarSign, Home, Building, PartyPopper, Calendar, Pencil, X, Save, Loader2, Shield, ChefHat, Sun, History } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtEUR = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: v % 1 ? 2 : 0 }).format(v);

const SEASON_LABELS = { haute: 'Haute saison', intermediaire: 'Intermediaire', basse: 'Basse saison' };
// Python-compatible rounding (banker's rounding: .5 rounds to nearest even)
const pyRound = (x) => { const f = Math.floor(x); const d = x - f; if (d === 0.5) return f % 2 === 0 ? f : f + 1; return Math.round(x); };
const ACC_LABELS = { 'appartement-1': 'Appt 1er', 'appartement-2': 'Appt 2eme', 'gite-seul': 'Gite seul', 'gite-salle': 'Gite + Salle', 'salle-seule': 'Salle seule' };
const EVENT_LABELS = {
  mariage: 'Mariage', seminaire_jour: 'Seminaire jour', seminaire_hebergement: 'Seminaire hebergement',
  anniversaire_salle: 'Anniversaire salle', anniversaire_gite_salle: 'Anniversaire gite + salle',
};

function PriceInput({ value, onChange, suffix = '', className = '', step = '1' }) {
  return (
    <div className="relative">
      <Input
        type="number"
        value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        className={`h-8 text-sm text-right ${suffix ? 'pr-7' : 'pr-2'} ${className}`}
        step={step}
        data-testid="price-input"
      />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#595959] pointer-events-none">{suffix}</span>}
    </div>
  );
}

const HISTORY_LABELS = {
  apartments: 'Appartements', gite_seul: 'Gite seul', events: 'Evenements', cautions: 'Caution',
  payment_conditions: 'Conditions de paiement', tourist_tax: 'Taxe de sejour', options: 'Options',
  price_per_night: 'Prix par nuit', extra_bed_per_night: 'Supplement occupant / nuit', cleaning_fee: 'Menage + linge',
  capacity: 'Capacite max', base_rate_by_season: 'Tarif de base', wood_per_day: 'Bois / jour',
  mariage_by_season: 'Mariage', mariage_cleaning: 'Menage mariage', mariage_sono: 'Sono / DJ', mariage_vaisselle_pp: 'Vaisselle / pers',
  seminaire_jour_tiers: 'Seminaire journee', seminaire_extra_pp_night: 'Seminaire suppl. / pers / nuit',
  reduction_mariage: 'Reduction jours supp. mariage (%)', reduction_gite: 'Reduction nuits supp. gite (%)',
  anniversaire_salle: 'Anniversaire salle', anniversaire_gite_salle: 'Anniversaire gite + salle',
  reveillon_forfait: 'Forfait Reveillon', cuisine_per_day: 'Cuisine pro / jour',
  deposit_rate: "Taux d'acompte", refundable: 'Remboursable',
  balance_deadlines_event: 'Delai solde evenement', balance_deadlines_acc: 'Delai solde hebergement',
  haute: 'Haute saison', intermediaire: 'Intermediaire', basse: 'Basse saison',
  enabled: 'Activee', rate: 'Taux (%)', cap_per_person_per_night: 'Plafond / pers / nuit',
  departmental_rate: 'Majoration dept. (%)', exemption_age: 'Age exoneration',
  recharge_per_night: 'Recharge elec. / nuit', eggs_per_unit: 'Oeufs (unite)',
  max: "Jusqu'a (pers.)", price: 'Prix',
};
const labelizeField = (path) =>
  path.replace(/\[(\d+)\]/g, '.palier $1').split('.')
    .map(seg => HISTORY_LABELS[seg] || ACC_LABELS[seg] || EVENT_LABELS[seg] || seg)
    .join(' › ');
const fmtHistValue = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  return String(v);
};

function PricingHistorySection({ api, refreshKey }) {
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    api('get', '/api/admin/pricing-history?limit=100').then(r => setEntries(r.data)).catch(() => {});
  }, [api, refreshKey]);
  const shown = expanded ? entries : entries.slice(0, 5);
  return (
    <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="pricing-history-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center"><History className="w-5 h-5 text-slate-600" strokeWidth={1.5} /></div>
          <div>
            <h2 data-testid="pricing-history-title" className="font-semibold text-[#2C3E30] text-lg">Historique des modifications</h2>
            <p className="text-xs text-[#595959]">Journal d'audit des prix, cautions et delais (depuis le 4 juillet 2026)</p>
          </div>
        </div>
        <Badge className="bg-slate-100 text-slate-700 border-slate-200 border" data-testid="pricing-history-count">{entries.length} sauvegarde{entries.length > 1 ? 's' : ''}</Badge>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-[#595959]" data-testid="pricing-history-empty">Aucune modification enregistree pour le moment. Les prochains changements de tarifs apparaitront ici.</p>
      ) : (
        <div className="space-y-3">
          {shown.map(entry => (
            <div key={entry.id} className="p-3 rounded-lg bg-[#F5F5F0]" data-testid="pricing-history-entry">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-[#2C3E30]">
                  {new Date(entry.changed_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
                <span className="text-[10px] text-[#595959]">par {entry.author}</span>
              </div>
              <div className="space-y-1">
                {entry.changes.map((c, i) => (
                  <div key={i} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                    <span className="text-[#595959]">{labelizeField(c.field)} :</span>
                    <span className="line-through text-[#993333]">{fmtHistValue(c.old_value)}</span>
                    <span className="text-[#595959]">→</span>
                    <span className="font-semibold text-emerald-700">{fmtHistValue(c.new_value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {entries.length > 5 && (
            <Button size="sm" variant="outline" onClick={() => setExpanded(e => !e)} data-testid="pricing-history-toggle">
              {expanded ? 'Reduire' : `Afficher tout (${entries.length})`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function SectionActions({ editing, onEdit, onSave, onCancel, saving, testId }) {
  if (editing) return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={onCancel} disabled={saving} data-testid={`${testId}-cancel`}>
        <X className="w-3.5 h-3.5 mr-1" /> Annuler
      </Button>
      <Button size="sm" onClick={onSave} disabled={saving} className="bg-[#2C3E30] hover:bg-[#3d5240] text-white" data-testid={`${testId}-save`}>
        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
        Sauvegarder
      </Button>
    </div>
  );
  return (
    <Button size="sm" variant="outline" onClick={onEdit} data-testid={`${testId}-edit`} className="border-[#C4A962]/40 text-[#2C3E30] hover:bg-[#C4A962]/10">
      <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
    </Button>
  );
}

export default function AdminPricing() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const [config, setConfig] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editSection, setEditSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cuisineSettings, setCuisineSettings] = useState(null);
  const [cuisineEdit, setCuisineEdit] = useState(null);
  const [cuisineEditing, setCuisineEditing] = useState(false);
  const [cuisineSaving, setCuisineSaving] = useState(false);
  const [seasonConfig, setSeasonConfig] = useState(null);
  const [seasonEdit, setSeasonEdit] = useState(null);
  const [seasonEditing, setSeasonEditing] = useState(false);
  const [seasonSaving, setSeasonSaving] = useState(false);
  const [optionAvail, setOptionAvail] = useState(null);
  const [optionSaving, setOptionSaving] = useState(false);
  const [rechargePriceEdit, setRechargePriceEdit] = useState(null);
  const [eggsPriceEdit, setEggsPriceEdit] = useState(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const api = useCallback((method, url, data) =>
    axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}` } }),
  [token]);

  const loadConfig = useCallback(() => {
    api('get', '/api/admin/pricing-config').then(r => setConfig(r.data)).catch(() => {});
  }, [api]);

  const loadCuisine = useCallback(() => {
    api('get', '/api/admin/cuisine-settings').then(r => setCuisineSettings(r.data)).catch(() => {});
  }, [api]);

  const loadSeasons = useCallback(() => {
    api('get', '/api/admin/season-config').then(r => setSeasonConfig(r.data)).catch(() => {});
  }, [api]);

  const loadOptionAvail = useCallback(() => {
    api('get', '/api/admin/option-availability').then(r => setOptionAvail(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadConfig();
    loadCuisine();
    loadSeasons();
    loadOptionAvail();
  }, [token, navigate, loadConfig, loadCuisine, loadSeasons, loadOptionAvail]);

  const startEdit = (section) => {
    setEditData(JSON.parse(JSON.stringify(config)));
    setEditSection(section);
  };

  const cancelEdit = () => { setEditData(null); setEditSection(null); };

  const otaReminder = () => {
    toast.info("Pensez a reporter ce changement sur Airbnb et Booking.com : l'iCal synchronise les disponibilites, pas les prix.", { duration: 10000 });
  };

  const saveSection = async () => {
    setSaving(true);
    try {
      const payload = JSON.parse(JSON.stringify(editData));
      // Clean up old gite format - remove prices_by_season, keep base_rate_by_season
      if (payload.gite_seul) {
        delete payload.gite_seul.prices_by_season;
      }
      // Clean up old reduction_weekend field
      if (payload.events) {
        delete payload.events.reduction_weekend;
      }
      await api('put', '/api/admin/pricing-config', payload);
      setConfig(JSON.parse(JSON.stringify(payload)));
      setEditSection(null);
      setEditData(null);
      setHistoryRefresh(k => k + 1);
      toast.success('Tarifs mis a jour avec succes');
      otaReminder();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const startCuisineEdit = () => {
    setCuisineEdit({ ...cuisineSettings });
    setCuisineEditing(true);
  };

  const cancelCuisineEdit = () => {
    setCuisineEdit(null);
    setCuisineEditing(false);
  };

  const saveCuisine = async () => {
    setCuisineSaving(true);
    try {
      const { key, updated_at, _id, ...payload } = cuisineEdit;
      await api('put', '/api/admin/cuisine-settings', payload);
      setCuisineSettings({ ...cuisineEdit });
      setCuisineEditing(false);
      setCuisineEdit(null);
      toast.success('Parametres cuisine mis a jour');
      otaReminder();
      loadConfig(); // Refresh pricing config since price_per_day syncs
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
    setCuisineSaving(false);
  };

  const MONTH_NAMES = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
  const SEASON_COLORS = {
    haute: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', dot: 'bg-orange-400' },
    intermediaire: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', dot: 'bg-amber-400' },
    basse: { bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-700', dot: 'bg-sky-400' },
  };

  const startSeasonEdit = () => {
    setSeasonEdit(JSON.parse(JSON.stringify(seasonConfig)));
    setSeasonEditing(true);
  };

  const toggleMonthSeason = (month, season) => {
    setSeasonEdit(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      for (const s of Object.keys(next)) {
        next[s] = next[s].filter(m => m !== month);
      }
      next[season].push(month);
      next[season].sort((a, b) => a - b);
      return next;
    });
  };

  const saveSeason = async () => {
    // Frontend validation: each season must have at least 1 month
    const empty = Object.entries(seasonEdit).filter(([, m]) => m.length === 0).map(([s]) => SEASON_LABELS[s]);
    if (empty.length > 0) {
      toast.error(`Chaque saison doit avoir au moins 1 mois. Saison(s) vide(s): ${empty.join(', ')}`);
      return;
    }
    setSeasonSaving(true);
    try {
      await api('put', '/api/admin/season-config', seasonEdit);
      setSeasonConfig(JSON.parse(JSON.stringify(seasonEdit)));
      setSeasonEditing(false);
      setSeasonEdit(null);
      toast.success('Saisons mises a jour');
      otaReminder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSeasonSaving(false);
  };

  const cancelSeasonEdit = () => { setSeasonEdit(null); setSeasonEditing(false); };

  const OPTION_LABELS = {
    cuisine: { label: 'Cuisine professionnelle', desc: 'Location de la cuisine equipee' },
    sono: { label: 'Sonorisation', desc: 'Systeme sono pour evenements' },
    vaisselle: { label: 'Vaisselle', desc: 'Service vaisselle pour receptions' },
    bois: { label: 'Bois / Buches', desc: 'Bois de chauffage pour cheminee' },
    recharge: { label: 'Recharge electrique', desc: `Borne de recharge vehicule (${optionAvail?.recharge_price_per_night || 5} EUR/nuit)` },
    oeufs: { label: 'Oeufs frais du domaine', desc: `Vendus a l'unite, max 150 par sejour (${optionAvail?.eggs_price_per_unit || 1} EUR/oeuf)` },
  };

  const toggleOptionAvail = async (key) => {
    const newVal = !optionAvail[key];
    setOptionSaving(true);
    try {
      const res = await api('put', '/api/admin/option-availability', { ...optionAvail, [key]: newVal });
      setOptionAvail(res.data);
      toast.success(`${OPTION_LABELS[key].label}: ${newVal ? 'Disponible' : 'Indisponible'}`);
    } catch { toast.error('Erreur lors de la mise a jour'); }
    setOptionSaving(false);
  };

  const getMonthSeason = (month, cfg) => {
    for (const [s, months] of Object.entries(cfg || {})) {
      if (months.includes(month)) return s;
    }
    return 'basse';
  };

  const u = (path, value) => {
    setEditData(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const p = path.split('.');
      let o = c;
      for (let i = 0; i < p.length - 1; i++) o = o[p[i]];
      o[p[p.length - 1]] = value;
      return c;
    });
  };

  const logout = () => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_email'); navigate('/admin'); };

  if (!config) return <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center"><p className="text-[#595959]">Chargement...</p></div>;

  const d = editSection ? editData : config;
  const isEdit = (s) => editSection === s;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="pricing-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl flex items-center gap-2">
              <DollarSign className="w-5 h-5" strokeWidth={1.5} /> Grille tarifaire
            </h1>
          </div>
          <Button onClick={logout} variant="ghost" className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10" size="sm">Deconnexion</Button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-8 space-y-6">

        {/* ── SAISONS ── */}
        {seasonConfig && (
          <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-seasons">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center"><Sun className="w-5 h-5 text-orange-600" strokeWidth={1.5} /></div>
                <div>
                  <h2 data-testid="pricing-seasons" className="font-semibold text-[#2C3E30] text-lg">Periodes saisonnieres</h2>
                  <p className="text-xs text-[#595959]">Definissez quels mois correspondent a chaque saison</p>
                </div>
              </div>
              <SectionActions
                editing={seasonEditing}
                onEdit={startSeasonEdit}
                onSave={saveSeason}
                onCancel={cancelSeasonEdit}
                saving={seasonSaving}
                testId="seasons"
              />
            </div>

            {(() => {
              const cfg = seasonEditing ? seasonEdit : seasonConfig;
              return (
                <div className="space-y-4">
                  {/* Legend */}
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(SEASON_LABELS).map(([key, label]) => {
                      const isEmpty = seasonEditing && (cfg[key] || []).length === 0;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${SEASON_COLORS[key].dot}`} />
                          <span className="text-sm text-[#595959]">{label}</span>
                          <Badge className={`text-[10px] border ${isEmpty ? 'bg-red-50 text-red-600 border-red-300' : `${SEASON_COLORS[key].bg} ${SEASON_COLORS[key].text} ${SEASON_COLORS[key].border}`}`}>
                            {(cfg[key] || []).length} mois
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                  {seasonEditing && Object.values(cfg).some(m => m.length === 0) && (
                    <p className="text-xs text-red-500 font-medium" data-testid="season-validation-error">
                      Chaque saison doit contenir au moins 1 mois pour pouvoir sauvegarder.
                    </p>
                  )}

                  {/* Month grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="season-month-grid">
                    {MONTH_NAMES.map((name, idx) => {
                      const month = idx + 1;
                      const season = getMonthSeason(month, cfg);
                      const colors = SEASON_COLORS[season];
                      return (
                        <div
                          key={month}
                          data-testid={`season-month-${month}`}
                          className={`relative rounded-lg p-3 text-center border-2 transition-all ${colors.bg} ${colors.border} ${seasonEditing ? 'cursor-pointer' : ''}`}
                        >
                          <p className={`text-sm font-medium ${colors.text}`}>{name}</p>
                          <p className={`text-[10px] mt-0.5 ${colors.text} opacity-70`}>{SEASON_LABELS[season]}</p>
                          {seasonEditing && (
                            <div className="flex justify-center gap-1 mt-2">
                              {Object.entries(SEASON_LABELS).map(([s, label]) => (
                                <button
                                  key={s}
                                  data-testid={`month-${month}-set-${s}`}
                                  onClick={() => toggleMonthSeason(month, s)}
                                  title={label}
                                  className={`w-5 h-5 rounded-full border-2 transition-all ${
                                    season === s
                                      ? `${SEASON_COLORS[s].dot} border-[#2C3E30] scale-110`
                                      : `${SEASON_COLORS[s].dot} opacity-30 border-transparent hover:opacity-70`
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── DISPONIBILITE DES OPTIONS ── */}
        {optionAvail && (
          <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-option-availability">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center"><Shield className="w-5 h-5 text-emerald-600" strokeWidth={1.5} /></div>
              <div>
                <h2 data-testid="pricing-option-avail" className="font-semibold text-[#2C3E30] text-lg">Disponibilite des options</h2>
                <p className="text-xs text-[#595959]">Activez ou desactivez les options proposees aux clients</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(OPTION_LABELS).map(([key, { label, desc }]) => (
                <div
                  key={key}
                  data-testid={`option-avail-${key}`}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    optionAvail[key] ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div>
                    <p className={`text-sm font-medium ${optionAvail[key] ? 'text-[#2C3E30]' : 'text-gray-400'}`}>{label}</p>
                    <p className="text-xs text-[#595959]">{desc}</p>
                  </div>
                  <Switch
                    data-testid={`option-toggle-${key}`}
                    checked={optionAvail[key]}
                    onCheckedChange={() => toggleOptionAvail(key)}
                    disabled={optionSaving}
                  />
                </div>
              ))}
            </div>
            {/* Recharge price config */}
            <div className="mt-4 p-4 rounded-lg border-2 border-[#E5E5E0] bg-[#FAFAF9]" data-testid="recharge-price-section">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2C3E30]">Prix recharge electrique</p>
                  <p className="text-xs text-[#595959]">Tarif par nuit facture selon la duree du sejour</p>
                </div>
                {rechargePriceEdit !== null ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={rechargePriceEdit}
                      onChange={(e) => setRechargePriceEdit(Number(e.target.value))}
                      className="w-20 text-sm border border-[#E5E5E0] rounded px-2 py-1 text-center"
                      data-testid="recharge-price-input"
                    />
                    <span className="text-xs text-[#595959]">EUR/nuit</span>
                    <Button size="sm" className="bg-[#2C3E30] hover:bg-[#3d5240] text-white h-7 text-xs" data-testid="recharge-price-save" onClick={async () => {
                      try {
                        const cfg = config;
                        const updated = { ...cfg, options: { ...(cfg.options || {}), recharge_per_night: rechargePriceEdit }};
                        await api('put', '/api/admin/pricing-config', updated);
                        setConfig(updated);
                        setRechargePriceEdit(null);
                        if (optionAvail) setOptionAvail({ ...optionAvail, recharge_price_per_night: rechargePriceEdit });
                        toast.success(`Prix recharge mis a jour : ${rechargePriceEdit} EUR/nuit`);
                      } catch { toast.error('Erreur de sauvegarde'); }
                    }}>OK</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRechargePriceEdit(null)}>Annuler</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-[#2C3E30]" data-testid="recharge-price-display">{config?.options?.recharge_per_night || 5} EUR</span>
                    <span className="text-xs text-[#595959]">/nuit</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs ml-2" data-testid="recharge-price-edit" onClick={() => setRechargePriceEdit(config?.options?.recharge_per_night || 5)}>Modifier</Button>
                  </div>
                )}
              </div>
            </div>
            {/* Eggs price config */}
            <div className="mt-4 p-4 rounded-lg border-2 border-[#E5E5E0] bg-[#FAFAF9]" data-testid="eggs-price-section">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2C3E30]">Prix oeufs frais du domaine</p>
                  <p className="text-xs text-[#595959]">Prix unitaire (le client commande la quantite, max 150)</p>
                </div>
                {eggsPriceEdit !== null ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={eggsPriceEdit}
                      onChange={(e) => setEggsPriceEdit(Number(e.target.value))}
                      className="w-20 text-sm border border-[#E5E5E0] rounded px-2 py-1 text-center"
                      data-testid="eggs-price-input"
                    />
                    <span className="text-xs text-[#595959]">EUR/oeuf</span>
                    <Button size="sm" className="bg-[#2C3E30] hover:bg-[#3d5240] text-white h-7 text-xs" data-testid="eggs-price-save" onClick={async () => {
                      try {
                        const cfg = config;
                        const updated = { ...cfg, options: { ...(cfg.options || {}), eggs_per_unit: eggsPriceEdit }};
                        await api('put', '/api/admin/pricing-config', updated);
                        setConfig(updated);
                        setEggsPriceEdit(null);
                        if (optionAvail) setOptionAvail({ ...optionAvail, eggs_price_per_unit: eggsPriceEdit });
                        toast.success(`Prix oeufs mis a jour : ${eggsPriceEdit} EUR/oeuf`);
                      } catch { toast.error('Erreur de sauvegarde'); }
                    }}>OK</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEggsPriceEdit(null)}>Annuler</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-[#2C3E30]" data-testid="eggs-price-display">{config?.options?.eggs_per_unit || 1} EUR</span>
                    <span className="text-xs text-[#595959]">/oeuf</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs ml-2" data-testid="eggs-price-edit" onClick={() => setEggsPriceEdit(config?.options?.eggs_per_unit || 1)}>Modifier</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── APPARTEMENTS ── */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-apartments">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center"><Building className="w-5 h-5 text-blue-600" strokeWidth={1.5} /></div>
              <div>
                <h2 data-testid="pricing-apartments" className="font-semibold text-[#2C3E30] text-lg">Appartements (Terrasse et Panorama)</h2>
                <p className="text-xs text-[#595959]">Max {d.apartments.capacity} pers. | Basse saison (nov-mars) | Lun-Ven</p>
              </div>
            </div>
            <SectionActions editing={isEdit('apartments')} onEdit={() => startEdit('apartments')} onSave={saveSection} onCancel={cancelEdit} saving={saving} testId="apartments" />
          </div>

          {isEdit('apartments') ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Prix par nuit (1 a 4 pers.)</label>
                  <PriceInput value={d.apartments.price_per_night} onChange={val => u('apartments.price_per_night', val)} suffix="EUR" data-testid="apt-price-per-night-input" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Supplement / pers. / nuit (des la 5e pers.)</label>
                  <PriceInput value={d.apartments.extra_bed_per_night} onChange={val => u('apartments.extra_bed_per_night', val)} suffix="EUR" data-testid="apt-extra-occupant-input" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Menage + linge</label>
                  <PriceInput value={d.apartments.cleaning_fee} onChange={val => u('apartments.cleaning_fee', val)} suffix="EUR" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Capacite max</label>
                  <PriceInput value={d.apartments.capacity} onChange={val => u('apartments.capacity', val)} suffix="pers" />
                </div>
              </div>
              <p className="text-[10px] text-[#8A9A5B]">Le supplement s'applique par occupant (adultes + enfants) au-dela de 4 personnes. Ex : 6 pers. = {fmtEUR((Number(d.apartments.price_per_night) || 0) + 2 * (Number(d.apartments.extra_bed_per_night) || 0))}/nuit</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-[#F5F5F0] text-center" data-testid="apt-price-per-night-view">
                  <p className="text-xs text-[#595959] mb-1">Prix / nuit (1-4 pers.)</p>
                  <p className="text-lg font-semibold text-[#2C3E30]">{fmtEUR(d.apartments.price_per_night)}</p>
                </div>
                {[5, 6, 7, 8].map(p => (
                  <div key={p} className="p-3 rounded-lg bg-[#F5F5F0] text-center">
                    <p className="text-xs text-[#595959] mb-1">{p} pers. / nuit</p>
                    <p className="text-lg font-semibold text-[#2C3E30]">{fmtEUR((Number(d.apartments.price_per_night) || 0) + (p - 4) * (Number(d.apartments.extra_bed_per_night) || 0))}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                <Badge className="bg-blue-50 text-blue-700 border-blue-100 border">Supplement des la 5e pers. : {fmtEUR(d.apartments.extra_bed_per_night)}/nuit/pers</Badge>
                <Badge className="bg-amber-50 text-amber-700 border-amber-100 border">Menage : {fmtEUR(d.apartments.cleaning_fee)}</Badge>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 border">Caution : {fmtEUR(d.cautions['appartement-1'])}</Badge>
              </div>
            </>
          )}
        </div>

        {/* ── GITE SEUL ── */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-gite">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center"><Home className="w-5 h-5 text-emerald-600" strokeWidth={1.5} /></div>
              <div>
                <h2 data-testid="pricing-gite" className="font-semibold text-[#2C3E30] text-lg">Gite seul</h2>
                <p className="text-xs text-[#595959]">Max {d.gite_seul.capacity} pers. | 1ere nuit plein tarif, nuits supp. reduites</p>
              </div>
            </div>
            <SectionActions editing={isEdit('gite')} onEdit={() => startEdit('gite')} onSave={saveSection} onCancel={cancelEdit} saving={saving} testId="gite" />
          </div>

          {isEdit('gite') ? (
            <div className="space-y-4">
              <p className="text-xs font-medium text-[#595959]">Tarif de base par saison (prix de la 1ere nuit)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(d.gite_seul.base_rate_by_season || {}).map(([season, v]) => (
                  <div key={season} className="space-y-1">
                    <label className="text-xs text-[#595959] capitalize">{SEASON_LABELS[season] || season}</label>
                    <PriceInput value={v} onChange={val => u(`gite_seul.base_rate_by_season.${season}`, val)} suffix="EUR" data-testid={`gite-base-${season}`} />
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-[#E5E5E0]">
                <div className="space-y-1 max-w-[220px]">
                  <label className="text-xs text-[#595959]">Reduction nuits supplementaires</label>
                  <PriceInput value={d.events.reduction_gite ?? 15} onChange={val => u('events.reduction_gite', val)} suffix="%" data-testid="gite-reduction-input" />
                  <p className="text-[9px] text-[#8A9A5B]">1ere nuit plein tarif, nuits suivantes a -X%</p>
                </div>
              </div>
              {/* Apercu calcul dynamique */}
              {(() => {
                const reduct = (d.events.reduction_gite ?? 15) / 100;
                const seasons = Object.entries(d.gite_seul.base_rate_by_season || {});
                if (!seasons.length) return null;
                return (
                  <div className="p-3 rounded-lg bg-[#8A9A5B]/10 space-y-2">
                    <p className="text-xs font-medium text-[#2C3E30]">Apercu des tarifs calcules automatiquement :</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="text-[#595959]">
                            <th className="p-1.5 text-left font-medium">Nuits</th>
                            {seasons.map(([s]) => <th key={s} className="p-1.5 text-center font-medium capitalize">{SEASON_LABELS[s] || s}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {[1, 2, 3, 4, 7].map(n => (
                            <tr key={n} className="border-t border-[#E5E5E0]/60">
                              <td className="p-1.5 font-medium">{n} nuit{n > 1 ? 's' : ''}</td>
                              {seasons.map(([s, base]) => {
                                const discounted = pyRound(base * (1 - reduct));
                                const total = n === 1 ? base : base + (n - 1) * discounted;
                                return <td key={s} className="p-1.5 text-center font-semibold text-[#2C3E30]">{fmtEUR(total)}</td>;
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[9px] text-[#595959]">Ces prix sont calcules automatiquement. Seul le tarif 1 nuit est modifiable.</p>
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Menage + linge</label>
                  <PriceInput value={d.gite_seul.cleaning_fee} onChange={val => u('gite_seul.cleaning_fee', val)} suffix="EUR" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Bois cheminee / jour</label>
                  <PriceInput value={d.gite_seul.wood_per_day} onChange={val => u('gite_seul.wood_per_day', val)} suffix="EUR" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Capacite max</label>
                  <PriceInput value={d.gite_seul.capacity} onChange={val => u('gite_seul.capacity', val)} suffix="pers" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Tarifs de base par saison */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {Object.entries(d.gite_seul.base_rate_by_season || {}).map(([season, base]) => (
                  <div key={season} className="p-3 rounded-lg bg-[#F5F5F0] text-center" data-testid={`gite-rate-${season}`}>
                    <p className="text-xs text-[#595959] mb-1 capitalize">{SEASON_LABELS[season] || season}</p>
                    <p className="text-lg font-semibold text-[#2C3E30]">{fmtEUR(base)}<span className="text-xs font-normal text-[#595959]"> / 1ere nuit</span></p>
                  </div>
                ))}
              </div>
              {/* Grille de prix calcules */}
              {(() => {
                const reduct = (d.events.reduction_gite ?? 15) / 100;
                const seasons = Object.entries(d.gite_seul.base_rate_by_season || {});
                if (!seasons.length) return null;
                return (
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm border-collapse" data-testid="gite-computed-prices">
                      <thead>
                        <tr className="bg-[#2C3E30] text-[#F2F0E9]">
                          <th className="p-2.5 text-left font-medium">Nuits</th>
                          {seasons.map(([s]) => <th key={s} className="p-2.5 text-center font-medium capitalize">{SEASON_LABELS[s] || s}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3, 4, 7].map(n => {
                          const isFirst = n === 1;
                          return (
                            <tr key={n} className="border-b border-[#E5E5E0]">
                              <td className="p-2.5 font-medium">{n} nuit{n > 1 ? 's' : ''} {!isFirst && <span className="text-[10px] text-[#8A9A5B] font-normal ml-1">calcule</span>}</td>
                              {seasons.map(([s, base]) => {
                                const discounted = pyRound(base * (1 - reduct));
                                const total = n === 1 ? base : base + (n - 1) * discounted;
                                return <td key={s} className="p-2.5 text-center font-semibold">{fmtEUR(total)}</td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              <div className="flex flex-wrap gap-3 text-xs">
                <Badge className="bg-amber-50 text-amber-700 border-amber-100 border">Menage : {fmtEUR(d.gite_seul.cleaning_fee)}</Badge>
                <Badge className="bg-orange-50 text-orange-700 border-orange-100 border">Bois : {fmtEUR(d.gite_seul.wood_per_day)}/jour</Badge>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 border">Caution : {fmtEUR(d.cautions['gite-seul'])}</Badge>
                {(d.events.reduction_gite ?? 15) > 0 && (
                  <Badge className="bg-[#8A9A5B]/10 text-[#8A9A5B] border-[#8A9A5B]/20 border" data-testid="gite-reduction-badge">Reduction nuits supp. : -{d.events.reduction_gite ?? 15}%</Badge>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── EVENEMENTS ── */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-events">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C4A962]/10 flex items-center justify-center"><PartyPopper className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} /></div>
              <div>
                <h2 data-testid="pricing-events" className="font-semibold text-[#2C3E30] text-lg">Gite + Salle des fetes - Evenements</h2>
                <p className="text-xs text-[#595959]">20 loges + 150 invites</p>
              </div>
            </div>
            <SectionActions editing={isEdit('events')} onEdit={() => startEdit('events')} onSave={saveSection} onCancel={cancelEdit} saving={saving} testId="events" />
          </div>

          {isEdit('events') ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mariage */}
              <div className="p-4 rounded-lg bg-[#F5F5F0] space-y-3">
                <h3 className="font-semibold text-sm text-[#2C3E30]">Mariage <span className="font-normal text-xs text-[#8A9A5B]">(prix/jour)</span></h3>
                {Object.entries(d.events.mariage_by_season).map(([s, v]) => (
                  <div key={s} className="space-y-1">
                    <label className="text-[10px] text-[#595959] capitalize">{SEASON_LABELS[s] || s}</label>
                    <PriceInput value={v} onChange={val => u(`events.mariage_by_season.${s}`, val)} suffix="EUR" />
                  </div>
                ))}
                <div className="pt-2 border-t border-[#E5E5E0]">
                  <div className="space-y-1">
                    <label className="text-[10px] text-[#595959]">Reduction jours supplementaires</label>
                    <PriceInput value={d.events.reduction_mariage ?? 10} onChange={val => u('events.reduction_mariage', val)} suffix="%" />
                    <p className="text-[9px] text-[#8A9A5B]">1er jour plein, jours suivants reduits de ce %</p>
                  </div>
                </div>
                <div className="space-y-1"><label className="text-[10px] text-[#595959]">Menage</label><PriceInput value={d.events.mariage_cleaning} onChange={val => u('events.mariage_cleaning', val)} suffix="EUR" /></div>
                <div className="space-y-1"><label className="text-[10px] text-[#595959]">Sono / DJ</label><PriceInput value={d.events.mariage_sono} onChange={val => u('events.mariage_sono', val)} suffix="EUR" /></div>
                <div className="space-y-1"><label className="text-[10px] text-[#595959]">Vaisselle / pers.</label><PriceInput value={d.events.mariage_vaisselle_pp} onChange={val => u('events.mariage_vaisselle_pp', val)} suffix="EUR" step="0.1" /></div>
              </div>
              {/* Seminaire jour */}
              <div className="p-4 rounded-lg bg-[#F5F5F0] space-y-3">
                <h3 className="font-semibold text-sm text-[#2C3E30]">Seminaire journee (tranches)</h3>
                {d.events.seminaire_jour_tiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-[10px] text-[#595959]">Jusqu'a (pers.)</label>
                      <PriceInput value={t.max} onChange={val => { const arr = [...d.events.seminaire_jour_tiers]; arr[i] = { ...arr[i], max: val }; u('events.seminaire_jour_tiers', arr); }} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] text-[#595959]">Prix</label>
                      <PriceInput value={t.price} onChange={val => { const arr = [...d.events.seminaire_jour_tiers]; arr[i] = { ...arr[i], price: val }; u('events.seminaire_jour_tiers', arr); }} suffix="EUR" />
                    </div>
                  </div>
                ))}
              </div>
              {/* Seminaire hebergement (calcul dynamique) */}
              <div className="p-4 rounded-lg bg-[#F5F5F0] space-y-3">
                <h3 className="font-semibold text-sm text-[#2C3E30]">Seminaire avec hebergement</h3>
                <div className="p-3 rounded-lg bg-[#8A9A5B]/10 text-xs text-[#2C3E30] space-y-1">
                  <p className="font-medium">Calcul automatique :</p>
                  <p>Seminaire journee (selon participants) x nb jours</p>
                  <p>+ Gite seul (selon saison et nb nuits)</p>
                  <p className="text-[#595959] mt-1">Modifiez les tarifs du seminaire journee et du gite seul pour ajuster.</p>
                </div>
              </div>
              {/* Anniversaires */}
              <div className="p-4 rounded-lg bg-[#F5F5F0] space-y-3">
                <h3 className="font-semibold text-sm text-[#2C3E30]">Anniversaires <span className="font-normal text-xs text-[#8A9A5B]">(prix/jour)</span></h3>
                <p className="text-[10px] font-medium text-[#595959]">Salle seule :</p>
                {Object.entries(d.events.anniversaire_salle).map(([s, v]) => (
                  <div key={s} className="space-y-1">
                    <label className="text-[10px] text-[#595959] capitalize">{SEASON_LABELS[s] || s}</label>
                    <PriceInput value={v} onChange={val => u(`events.anniversaire_salle.${s}`, val)} suffix="EUR" />
                  </div>
                ))}
                <div className="pt-2 border-t border-[#E5E5E0]">
                  <p className="text-[10px] font-medium text-[#595959] mb-1">Gite + Salle (prix/jour, hebergement inclus) :</p>
                  {Object.entries(d.events.anniversaire_gite_salle || {}).map(([s, v]) => (
                    <div key={s} className="space-y-1 mb-2">
                      <label className="text-[10px] text-[#595959] capitalize">{SEASON_LABELS[s] || s}</label>
                      <PriceInput value={v} onChange={val => u(`events.anniversaire_gite_salle.${s}`, val)} suffix="EUR" data-testid={`anniv-gite-salle-${s}-input`} />
                    </div>
                  ))}
                </div>
              </div>
              {/* Reveillon */}
              <div className="p-4 rounded-lg bg-[#F5F5F0] space-y-3" data-testid="reveillon-edit-block">
                <h3 className="font-semibold text-sm text-[#2C3E30]">Forfait Reveillon <span className="font-normal text-xs text-[#8A9A5B]">(24/12 et 31/12)</span></h3>
                <div className="space-y-1">
                  <label className="text-[10px] text-[#595959]">Forfait</label>
                  <PriceInput value={d.events.reveillon_forfait ?? 3200} onChange={val => u('events.reveillon_forfait', val)} suffix="EUR" data-testid="reveillon-forfait-input" />
                </div>
                <p className="text-[9px] text-[#8A9A5B]">Applique automatiquement a tout sejour Gite + Salle incluant le 24/12 ou le 31/12. Remplace le tarif de la formule choisie (menage et options en sus).</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-[#F5F5F0]">
                <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">Mariage <span className="font-normal text-xs text-[#8A9A5B]">(prix/jour)</span></h3>
                <div className="space-y-1 text-sm">
                  {Object.entries(d.events.mariage_by_season).map(([s, v]) => (
                    <div key={s} className="flex justify-between"><span className="text-[#595959] capitalize">{SEASON_LABELS[s] || s}</span><span className="font-semibold">{fmtEUR(v)}</span></div>
                  ))}
                  {(d.events.reduction_mariage ?? 10) > 0 && (
                    <div className="flex justify-between pt-1 border-t border-[#E5E5E0] mt-1"><span className="text-[#8A9A5B]">Reduction jours supp.</span><span className="font-semibold text-[#8A9A5B]">-{d.events.reduction_mariage ?? 10}%</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-[#595959]">Menage</span><span className="font-semibold">{fmtEUR(d.events.mariage_cleaning)}</span></div>
                  <div className="flex justify-between"><span className="text-[#595959]">Sono/DJ</span><span className="font-semibold">{fmtEUR(d.events.mariage_sono)}</span></div>
                  <div className="flex justify-between"><span className="text-[#595959]">Vaisselle</span><span className="font-semibold">{d.events.mariage_vaisselle_pp} EUR/pers</span></div>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#F5F5F0]">
                <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">Seminaire journee</h3>
                <div className="space-y-1 text-sm">
                  {d.events.seminaire_jour_tiers.map((t, i) => (
                    <div key={i} className="flex justify-between"><span className="text-[#595959]">Jusqu'a {t.max} pers.</span><span className="font-semibold">{fmtEUR(t.price)}</span></div>
                  ))}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#F5F5F0]">
                <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">Seminaire avec hebergement</h3>
                <div className="p-2.5 rounded bg-[#8A9A5B]/10 text-xs text-[#2C3E30] space-y-0.5">
                  <p className="font-medium">Calcul dynamique :</p>
                  <p>= Seminaire journee x nb jours + Gite seul</p>
                  <p className="text-[#595959]">Ex: 2j/50 pers/basse = (950 x 2) + gite 2 nuits</p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#F5F5F0]">
                <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">Anniversaires <span className="font-normal text-xs text-[#8A9A5B]">(prix/jour)</span></h3>
                <div className="space-y-1 text-sm">
                  <p className="text-xs font-medium text-[#595959] mb-1">Salle seule :</p>
                  {Object.entries(d.events.anniversaire_salle).map(([s, v]) => (
                    <div key={s} className="flex justify-between"><span className="text-[#595959] capitalize">{SEASON_LABELS[s] || s}</span><span className="font-semibold">{fmtEUR(v)}</span></div>
                  ))}
                  <div className="pt-2 mt-2 border-t border-[#E5E5E0]">
                    <p className="text-xs font-medium text-[#595959] mb-1">Gite + Salle (hebergement inclus) :</p>
                    {Object.entries(d.events.anniversaire_gite_salle || {}).map(([s, v]) => (
                      <div key={s} className="flex justify-between"><span className="text-[#595959] capitalize">{SEASON_LABELS[s] || s}</span><span className="font-semibold">{fmtEUR(v)}</span></div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#F5F5F0]" data-testid="reveillon-view-block">
                <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">Forfait Reveillon <span className="font-normal text-xs text-[#8A9A5B]">(24/12 et 31/12)</span></h3>
                <div className="flex justify-between text-sm mb-2"><span className="text-[#595959]">Forfait</span><span className="font-semibold" data-testid="reveillon-forfait-view">{fmtEUR(d.events.reveillon_forfait ?? 3200)}</span></div>
                <div className="p-2 rounded bg-[#8A9A5B]/10 text-[10px] text-[#2C3E30]">
                  Applique automatiquement pour tout sejour Gite + Salle incluant le 24/12 ou le 31/12 (remplace le tarif de la formule choisie).
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── CUISINE PROFESSIONNELLE ── */}
        {cuisineSettings && (
          <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-cuisine">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C4A962]/10 flex items-center justify-center"><ChefHat className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} /></div>
                <div>
                  <h2 data-testid="pricing-cuisine" className="font-semibold text-[#2C3E30] text-lg">Cuisine professionnelle</h2>
                  <p className="text-xs text-[#595959]">Option Gite + Salle uniquement</p>
                </div>
                {!cuisineEditing && (
                  <Badge className={`ml-2 text-xs border ${cuisineSettings.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-[#993333] border-red-100'}`}>
                    {cuisineSettings.enabled ? 'Actif' : 'Desactive'}
                  </Badge>
                )}
              </div>
              {cuisineEditing ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={cancelCuisineEdit} disabled={cuisineSaving} data-testid="cuisine-cancel">
                    <X className="w-3.5 h-3.5 mr-1" /> Annuler
                  </Button>
                  <Button size="sm" onClick={saveCuisine} disabled={cuisineSaving} className="bg-[#2C3E30] hover:bg-[#3d5240] text-white" data-testid="cuisine-save">
                    {cuisineSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                    Sauvegarder
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={startCuisineEdit} data-testid="cuisine-edit" className="border-[#C4A962]/40 text-[#2C3E30] hover:bg-[#C4A962]/10">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                </Button>
              )}
            </div>

            {cuisineEditing && cuisineEdit ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F5F5F0]">
                  <Switch
                    checked={cuisineEdit.enabled}
                    onCheckedChange={v => setCuisineEdit(prev => ({ ...prev, enabled: v }))}
                    data-testid="cuisine-enabled-toggle"
                  />
                  <span className="text-sm text-[#2C3E30]">Afficher le bloc sur la page Tarifs publique</span>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Titre</label>
                  <Input
                    value={cuisineEdit.title || ''}
                    onChange={e => setCuisineEdit(prev => ({ ...prev, title: e.target.value }))}
                    className="h-9 text-sm"
                    data-testid="cuisine-title-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#595959]">Description</label>
                  <Textarea
                    value={cuisineEdit.description || ''}
                    onChange={e => setCuisineEdit(prev => ({ ...prev, description: e.target.value }))}
                    className="text-sm min-h-[80px]"
                    data-testid="cuisine-description-input"
                  />
                </div>
                <div className="space-y-1 max-w-[200px]">
                  <label className="text-xs text-[#595959]">Prix par jour</label>
                  <PriceInput
                    value={cuisineEdit.price_per_day || 300}
                    onChange={val => setCuisineEdit(prev => ({ ...prev, price_per_day: val }))}
                    suffix="EUR"
                  />
                  <p className="text-[10px] text-[#8A9A5B]">Synchronise avec le calcul de reservation</p>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-[#F5F5F0]">
                <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">{cuisineSettings.title || 'Cuisine professionnelle'}</h3>
                <p className="text-sm text-[#595959] mb-3">{cuisineSettings.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-[#595959] text-sm">Prix par jour</span>
                  <span className="text-lg font-semibold text-[#2C3E30]">{fmtEUR(cuisineSettings.price_per_day || 300)}</span>
                </div>
                <p className="text-xs text-[#8A9A5B] mt-2">* Option disponible uniquement avec la reservation du Gite + Salle.</p>
              </div>
            )}
          </div>
        )}

        {/* ── TAXE DE SEJOUR ── */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-tourist-tax">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-teal-600" strokeWidth={1.5} /></div>
              <div>
                <h2 data-testid="pricing-tourist-tax" className="font-semibold text-[#2C3E30] text-lg">Taxe de sejour</h2>
                <p className="text-xs text-[#595959]">Saint-Etienne Metropole - Meuble non classe</p>
              </div>
            </div>
            <SectionActions editing={isEdit('tourist_tax')} onEdit={() => startEdit('tourist_tax')} onSave={saveSection} onCancel={cancelEdit} saving={saving} testId="tourist-tax" />
          </div>

          {isEdit('tourist_tax') ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#F5F5F0]">
                <span className="text-sm text-[#595959]">Activer la taxe de sejour</span>
                <Switch checked={d.tourist_tax?.enabled ?? true} onCheckedChange={val => u('tourist_tax.enabled', val)} data-testid="tourist-tax-enabled-switch" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] text-[#595959]">Taux (%)</label>
                  <PriceInput value={d.tourist_tax?.rate ?? 5.5} onChange={val => u('tourist_tax.rate', val)} suffix="%" step="0.1" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-[#595959]">Plafond/pers/nuit</label>
                  <PriceInput value={d.tourist_tax?.cap_per_person_per_night ?? 5.39} onChange={val => u('tourist_tax.cap_per_person_per_night', val)} suffix="EUR" step="0.01" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-[#595959]">Taxe departementale (%)</label>
                  <PriceInput value={d.tourist_tax?.departmental_rate ?? 10} onChange={val => u('tourist_tax.departmental_rate', val)} suffix="%" step="0.1" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-[#595959]">Age exoneration</label>
                  <PriceInput value={d.tourist_tax?.exemption_age ?? 18} onChange={val => u('tourist_tax.exemption_age', val)} suffix="ans" />
                </div>
              </div>
              <p className="text-[10px] text-[#8A9A5B]">Calcul: min(tarif hebergement/pers/nuit x taux, plafond) x (1 + taxe dept). Exoneration des mineurs.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-[#F5F5F0] text-center">
                <p className="text-xs text-[#595959] mb-1">Statut</p>
                <Badge className={`text-xs border ${(d.tourist_tax?.enabled ?? true) ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-[#993333] border-red-100'}`}>
                  {(d.tourist_tax?.enabled ?? true) ? 'Active' : 'Desactivee'}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-[#F5F5F0] text-center">
                <p className="text-xs text-[#595959] mb-1">Taux</p>
                <p className="text-lg font-semibold text-[#2C3E30]">{d.tourist_tax?.rate ?? 5.5}%</p>
              </div>
              <div className="p-3 rounded-lg bg-[#F5F5F0] text-center">
                <p className="text-xs text-[#595959] mb-1">Plafond</p>
                <p className="text-lg font-semibold text-[#2C3E30]">{fmtEUR(d.tourist_tax?.cap_per_person_per_night ?? 5.39)}/pers/nuit</p>
              </div>
              <div className="p-3 rounded-lg bg-[#F5F5F0] text-center">
                <p className="text-xs text-[#595959] mb-1">Taxe dept.</p>
                <p className="text-lg font-semibold text-[#2C3E30]">+{d.tourist_tax?.departmental_rate ?? 10}%</p>
              </div>
            </div>
          )}
        </div>

        {/* ── CAUTIONS ── */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-cautions">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center"><Shield className="w-5 h-5 text-orange-600" strokeWidth={1.5} /></div>
              <h2 data-testid="pricing-cautions" className="font-semibold text-[#2C3E30] text-lg">Cautions</h2>
            </div>
            <SectionActions editing={isEdit('cautions')} onEdit={() => startEdit('cautions')} onSave={saveSection} onCancel={cancelEdit} saving={saving} testId="cautions" />
          </div>

          {isEdit('cautions') ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(d.cautions).map(([k, v]) => (
                <div key={k} className="space-y-1">
                  <label className="text-xs text-[#595959]">{ACC_LABELS[k] || k}</label>
                  <PriceInput value={v} onChange={val => u(`cautions.${k}`, val)} suffix="EUR" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(d.cautions).map(([k, v]) => (
                <div key={k} className="p-3 rounded-lg bg-[#F5F5F0] text-center">
                  <p className="text-xs text-[#595959] mb-1">{ACC_LABELS[k] || k}</p>
                  <p className="text-lg font-semibold text-[#2C3E30]">{fmtEUR(v)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── CONDITIONS DE PAIEMENT ── */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="section-conditions">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center"><Calendar className="w-5 h-5 text-purple-600" strokeWidth={1.5} /></div>
              <h2 data-testid="pricing-conditions" className="font-semibold text-[#2C3E30] text-lg">Conditions de paiement</h2>
            </div>
            <SectionActions editing={isEdit('conditions')} onEdit={() => startEdit('conditions')} onSave={saveSection} onCancel={cancelEdit} saving={saving} testId="conditions" />
          </div>

          {isEdit('conditions') ? (
            <div className="space-y-6">
              <div className="space-y-1">
                <label className="text-xs text-[#595959]">Taux d'acompte (%)</label>
                <PriceInput value={Math.round(d.payment_conditions.deposit_rate * 100)} onChange={val => u('payment_conditions.deposit_rate', val / 100)} suffix="%" />
              </div>
              <div>
                <p className="text-xs font-medium text-[#595959] mb-2">Acompte remboursable ?</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(d.payment_conditions.refundable).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between p-3 rounded-lg bg-[#F5F5F0]">
                      <span className="text-xs text-[#595959]">{ACC_LABELS[k] || k}</span>
                      <Switch checked={v} onCheckedChange={val => u(`payment_conditions.refundable.${k}`, val)} data-testid={`refundable-switch-${k}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-[#595959] mb-2">Delais de solde - Hebergements (jours avant arrivee)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(d.payment_conditions.balance_deadlines_acc).map(([k, v]) => (
                    <div key={k} className="space-y-1">
                      <label className="text-[10px] text-[#595959]">{ACC_LABELS[k] || k}</label>
                      <PriceInput value={v} onChange={val => u(`payment_conditions.balance_deadlines_acc.${k}`, val)} suffix="j" />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-[#595959] mb-2">Delais de solde - Evenements (jours avant arrivee)</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(d.payment_conditions.balance_deadlines_event).map(([k, v]) => (
                    <div key={k} className="space-y-1">
                      <label className="text-[10px] text-[#595959]">{EVENT_LABELS[k] || k}</label>
                      <PriceInput value={v} onChange={val => u(`payment_conditions.balance_deadlines_event.${k}`, val)} suffix="j" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-[#F5F5F0]">
                  <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">Acompte : {Math.round(d.payment_conditions.deposit_rate * 100)}%</h3>
                  <div className="space-y-1.5 text-sm">
                    {Object.entries(d.payment_conditions.refundable).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-[#595959]">{ACC_LABELS[k] || k}</span>
                        <Badge className={`text-xs border ${v ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-[#993333] border-red-100'}`}>
                          {v ? 'Remboursable' : 'Non remboursable'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-[#F5F5F0]">
                  <h3 className="font-semibold text-sm text-[#2C3E30] mb-2">Delais de solde</h3>
                  <div className="space-y-1.5 text-sm">
                    {Object.entries(d.payment_conditions.balance_deadlines_acc).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="text-[#595959]">{ACC_LABELS[k] || k}</span><span className="font-medium">{v} jours avant</span></div>
                    ))}
                    {Object.entries(d.payment_conditions.balance_deadlines_event).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="text-[#595959]">{EVENT_LABELS[k] || k}</span><span className="font-medium">{v} jours avant</span></div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-xs text-[#993333] mt-4 font-medium">Annulation automatique si le solde n'est pas regle dans les delais. L'acompte reste acquis pour les formules non remboursables.</p>
            </>
          )}
        </div>

        <PricingHistorySection api={api} refreshKey={historyRefresh} />
      </div>
    </div>
  );
}
